import React, { useState } from "react";
import '../assets/styles/Project.scss';

// --- UI COMPONENTS ---
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box'; 
import Typography from '@mui/material/Typography';

// --- ICONS ---
import YouTubeIcon from '@mui/icons-material/YouTube';
import CollectionsIcon from '@mui/icons-material/Collections'; 
import CloseIcon from '@mui/icons-material/Close'; 
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';

// --- IMAGE IMPORTS ---
import rlHero from '../assets/images/rl-hero.webp';
import rlStress from '../assets/images/rl-stress.webp';
import rlTruck from '../assets/images/rl-truck.webp';

import nmpcHero from '../assets/images/nmpc-hero.webp';
import nmpcStress from '../assets/images/nmpc-stress.webp';
import nmpcArch from '../assets/images/nmpc-arch.webp';
import nmpcStats from '../assets/images/nmpc-stats.webp';
import posterNMPC from '../assets/images/poster-nmpc.webp';

import ekfHero from '../assets/images/ekf-hero.webp';
import ekfTable from '../assets/images/ekf-table.webp';
import ekfBench from '../assets/images/ekf-bench.webp';
import ekfSensor from '../assets/images/ekf-sensor.webp';
import ekfModel from '../assets/images/ekf-model.webp';

import vibArch from '../assets/images/vib-arch.webp';
import vibConf from '../assets/images/vib-conf.webp';
import vibMel from '../assets/images/vib-mel.webp';
import vibDashCnn from '../assets/images/vib-dash-cnn.webp';
import vibDashAuto from '../assets/images/vib-dash-auto.webp';

import caneAssembly from '../assets/images/cane-assembly.webp';
import caneTesting from '../assets/images/cane-testing.webp';
import caneProblem from '../assets/images/cane-problem.webp';
import caneDetail from '../assets/images/cane-detail.webp';
import caneModules from '../assets/images/cane-modules.webp';
import caneFeaClip from '../assets/images/cane-fea-clip.webp';
import caneFeaThermal from '../assets/images/cane-fea-thermal.webp';
import posterCane from '../assets/images/poster-cane.webp';

import solarHero from '../assets/images/solar-hero.webp';
import solarTeam from '../assets/images/solar-team.webp';
import solarSuspCad from '../assets/images/solar-susp-cad.webp';
import solarSuspRear from '../assets/images/solar-susp-rear.webp';
import solarSuspReal from '../assets/images/solar-susp-real.webp';
import solarFea from '../assets/images/solar-fea.webp';

import nmpcLoopHeat from '../assets/images/nmpc-loop-heat.webp';   // Hero: Heating Loop
import nmpcVsPid from '../assets/images/nmpc-vs-pid.webp';         // Results
import nmpcMath from '../assets/images/nmpc-math.webp';             // Equations
import nmpcScale from '../assets/images/nmpc-scale.webp';           // Scaling/Robustness
import nmpcLoopCool from '../assets/images/nmpc-loop-cool.webp';     // Cooling Loop

import graphConcept from '../assets/images/graph-concept.webp';   // Hero: Infographic
import graphMatrix from '../assets/images/graph-matrix.webp';     // Matrix Stacking
import graphPipeline from '../assets/images/graph-pipeline.webp'; // Jinja/JAX Steps
import graphVcs from '../assets/images/graph-vcs.webp';           // VCS Simulation Result

// --- INTERFACES ---
interface CaptionMap {
    [key: string]: string;
}

// --- DATA STRUCTURE ---
const projects = [
    {
        title: "Hierarchical RL for BEV Thermal",
        brief: "Predictive control architecture utilizing road-grade look-ahead to prevent thermal derating and reduce energy by 37.6%.",
        image: rlTruck, 
        video: "", 
        gallery: [rlTruck, rlHero, rlStress], 
        captions: {
            [rlTruck]: "Figure 1: Concept visualization of the Heavy-Duty BEV simulation environment used for training and validation.",
            [rlHero]: "Figure 2: Proactive Thermal Management. The RL Agent (Blue) detects the upcoming 6% grade 500m in advance and pre-cools the battery, preventing the thermal violation that causes the PID controller (Grey) to fail.",
            [rlStress]: "Figure 3: Robustness Validation. Long-duration stress testing on stochastic 'Rolling Hills' terrain. The agent demonstrates policy stability and consistent energy savings over a 1-hour drive cycle under random traffic loads.",
        } as CaptionMap,
        details: `
            INNOVATION:
            Engineered a two-layer hierarchical control architecture combining a high-level Reinforcement Learning (PPO) agent for strategic setpoint optimization with a low-level PID layer for robust actuator tracking.

            KEY RESULTS:
            - Achieved 37.6% energy savings in worst-case degradation scenarios by utilizing road-grade look-ahead.
            - Demonstrated zero safety violations under stochastic uncertainty (traffic, ambient temp, sensor noise).
            - Outperformed industry-standard PID control by proactively managing thermal buffers before high-load events.
        `,
        tags: ["Reinforcement Learning", "Python", "Controls", "Safety-Critical"],
    },
    {
        title: "Deep Learning for Real-Time NMPC",
        brief: "Neural Network surrogate achieving 2,500x speedup over numerical solvers with certified safety.",
        image: nmpcArch, 
        video: "", 
        gallery: [nmpcArch, nmpcHero, nmpcStress, nmpcStats, posterNMPC], 
        captions: {
            [nmpcArch]: "Figure 1: Physics-Informed Architecture. We constrained the Multi-Layer Perceptron (MLP) weights to be Monotonic and Lipschitz bounded, guaranteeing closed-loop stability by design.",
            [nmpcHero]: "Figure 2: Validation Trajectory. The Neural Surrogate (Green) tracks the Optimal NMPC reference (Blue) with <0.2% error, replicating complex nonlinear control behavior.",
            [nmpcStress]: "Figure 3: Out-of-Distribution Safety Test. The controller successfully stabilizes the system from 950 K (approx. 2x nominal training temp), proving the robustness of the Lipschitz constraints where standard networks failed.",
            [nmpcStats]: "Figure 4: Monte Carlo Analysis (80 runs). Box plots of Control Effort and Tracking Error demonstrate the Neural Policy is statistically equivalent to the computation-heavy NMPC baseline.",
            [posterNMPC]: "Research Poster presenting the full mathematical formulation, training pipeline, and hardware deployment results on edge devices."
        } as CaptionMap,
        details: `
            OBJECTIVE:
            Enable complex optimal control rivaling a Nonlinear Model Predictive Control (NMPC) on resource-constrained automotive hardware by leveraging Neural Networks while maintaining safety guarantees.

            ACHIEVEMENTS:
            - Architected a Monotonic Lipschitz-constrained MLP network to guarantee safety in Out-of-Distribution (OOD) cases.
            - Achieved a 2,500x reduction in median computation time (0.2ms vs 500ms) compared to the NMPC (using SLSQP solver).
            - Validated Out-of-Distribution (OOD) safety, recovering from extreme initial conditions (950 K) that causes standard neural networks to saturate and fail.
        `,
        tags: ["Deep Learning", "PyTorch", "NMPC", "Real-Time Deployment"],
    },
    {
        title: "EKF State Estimation & Co-Design",
        brief: "Observer design and sensor placement optimization for nonlinear thermal energy storage.",
        image: ekfModel, 
        video: "", 
        gallery: [ekfModel, ekfHero, ekfTable, ekfBench, ekfSensor], 
        captions: {
            [ekfModel]: "Figure 1: System layout with Phase Change Material (PCM) as part of HVAC layout.",
            [ekfHero]: "Figure 2: Estimation Performance. The Extended Kalman Filter (Yellow) accurately tracks the True State of Charge (Red) through the highly nonlinear solid-liquid phase transition.",
            [ekfTable]: "Figure 3: Performance Benchmarking. Quantitative comparison showing EKF provides the optimal balance of accuracy (MSE: 1.658e-4) and computational load (78s) compared to UKF and SDRE.",
            [ekfBench]: "Figure 4: Filter Comparison. Trajectory analysis of different nonlinear estimators (EKF, UKF, SDRE) against the ground truth simulation.",
            [ekfSensor]: "Figure 5: Hardware Co-Design. Observability analysis determined the optimal thermocouple location (Node 5) to minimize estimation error, influencing the physical tank design.",
        } as CaptionMap,
        details: `
            ALGORITHM DESIGN:
            Developed an Extended Kalman Filter (EKF) to estimate the internal State of Charge (SoC) of a Phase Change Material (PCM) thermal battery, which exhibits highly nonlinear switching dynamics.

            HARDWARE CO-DESIGN:
            - Conducted observability analysis to determine the optimal sensor placement within the PCM tank.
            - Demonstrated that placing the sensor at the node of highest thermal gradient reduced steady-state estimation error.

            BENCHMARKING:
            - Compared EKF against Unscented Kalman Filter (UKF) and State-Dependent Riccati Equation (SDRE) methods.
        `,
        tags: ["Kalman Filter", "State Estimation", "Matlab", "Co-Design"],
    },
    {
        title: "Predictive Maintenance Edge AI",
        brief: "End-to-end IoT pipeline using CNNs and Autoencoders to detect vacuum pump anomalies.",
        image: vibArch, 
        video: "", 
        gallery: [vibArch, vibConf, vibMel, vibDashCnn, vibDashAuto], 
        captions: {
            [vibArch]: "Figure 1: End-to-End System Architecture. Data flows from industrial sensors via MTConnect agents to a SQL database, processed by TensorFlow Lite models, and visualized in Grafana.",
            [vibConf]: "Figure 2: Classification Performance. Confusion Matrix showing the CNN accurately distinguishing between 'Steady State' (Normal) and 'Leaking' conditions with high precision.",
            [vibMel]: "Figure 3: Feature Engineering. Raw accelerometer data is converted into Mel Spectrograms, revealing distinct frequency signatures used as inputs for the CNN.",
            [vibDashCnn]: "Figure 4: Real-Time Deployment. Grafana dashboard visualizing live inference results, flagging leakage events instantly to operators.",
            [vibDashAuto]: "Figure 5: Unsupervised Anomaly Detection. Autoencoder reconstruction error tracking on the Axial Flow Fan, identifying abnormal behavior without labeled training data."
        } as CaptionMap,
        details: `
            SYSTEM ARCHITECTURE:
            Designed and deployed a full-stack IoT predictive maintenance system for industrial vacuum pumps.
            - **Hardware:** Integrated ADXL accelerometers and environmental sensors with a Raspberry Pi gateway.
            - **Pipeline:** Implemented MTConnect adapters for standardized data collection into a SQL backend.

            ALGORITHMS:
            - **Supervised:** Trained a CNN on Mel Spectrogram features to classify vacuum leaks with ~95% accuracy.
            - **Unsupervised:** Developed an Autoencoder to monitor fan health by flagging high reconstruction errors (anomalies).
            
            DEPLOYMENT:
            - Models optimized via TensorFlow Lite for edge inference.
            - Built interactive Grafana dashboards for real-time condition monitoring.
        `,
        tags: ["Edge AI", "IoT", "TensorFlow", "Signal Processing"],
    },
    {
        title: "Smart Assistive Module",
        brief: "Haptic feedback attachment for white canes detecting head-level obstacles via ultrasonic sensing.",
        image: caneAssembly, 
        video: "", 
        gallery: [caneAssembly, caneTesting, caneProblem, caneDetail, caneModules, caneFeaClip, caneFeaThermal, posterCane], 
        captions: {
            [caneAssembly]: "Figure 1: Modular Assembly. The system attaches to standard white canes, integrating a Handle, Hpatic Feedback Module, and Sensor Module to detect overhangs that traditional sweeping misses.",
            [caneTesting]: "Figure 2: User Validation. Testing prototype ergonomics and feedback intensity with visually impaired users at the Bosma Center for Visionary Solutions.",
            [caneProblem]: "Figure 3: Problem Statement. Standard canes detect ground-level obstacles but miss 'overhangs' (branches, signs), leading to upper-body injury. This device fills that sensing gap.",
            [caneDetail]: "Figure 4: Sensor Module. Custom housing optimized usability.",
            [caneModules]: "Figure 5: System Architecture. Exploded view of the three modular components, designed for easy attachment and feedback.",
            [caneFeaClip]: "Figure 6: Structural Analysis (FEA). Stress simulation of the snap-fit clip mechanism under maximum deflection, ensuring durability over repeated attachment cycles.",
            [caneFeaThermal]: "Figure 7: Thermal Management. CFD analysis of the housing confirming electronics remain within safe operating temperatures during continuous use.",
            [posterCane]: "Project Poster detailing the user research, iterative prototyping process, and final validation results."
        } as CaptionMap,
        details: `
            PRODUCT ENGINEERING:
            Engineered a modular electromechanical attachment to solve the "overhang detection" gap for visually impaired users.
            
            USER-CENTRIC DESIGN & VALIDATION:
            - **Validation:** Conducted usability testing at the Bosma Center to refine haptic feedback patterns and ergonomic grip.
            - **Acoustics:** Designed a custom acoustic damping housing to directionalize ultrasonic waves and reduce false positives.

            MECHANICAL ANALYSIS:
            - **FEA:** Conducted Finite Element Analysis on the custom clipping mechanism to ensure fatigue resistance.
            - **Thermal:** Performed CFD simulations to ensure maintenance of PCB and critical electronics temperatures <40°C.
        `,
        tags: ["Product Design", "FEA/CFD", "Sensors", "Prototyping"],
    },
    {
        title: "Purdue Solar Racing (Apollo)",
        brief: "Mechanical Lead for the 'Apollo' Urban Concept vehicle. 2nd Place at Shell Eco-marathon.",
        image: solarHero, 
        video: "https://www.youtube.com/watch?v=sE4cFjdyLcQ", 
        gallery: [solarHero, solarSuspCad, solarSuspReal, solarFea, solarSuspRear, solarTeam], 
        captions: {
            [solarHero]: "Figure 1: The 'Apollo' Urban Concept. Competed at the Shell Eco-marathon Americas, securing a 2nd place podium finish in the Battery Electric Prototype category.",
            [solarSuspCad]: "Figure 2: Suspension Design (Front). Custom double-wishbone geometry later tuned for maximizing efficiency.",
            [solarSuspReal]: "Figure 3: Manufacturing Validation. As-built assembly of the front suspension, verifying the CNC-machined uprights and control arms against the CAD model.",
            [solarFea]: "Figure 4: Structural Analysis. FEA simulation of suspension hardpoints under bump and braking loads to ensure integrity.",
            [solarSuspRear]: "Figure 5: Rear Suspension Architecture. Integrated trailing-arm design accommodating the powertrain assembly while maximizing aerodynamic efficiency.",
            [solarTeam]: "Figure 6: Leadership. Led a cross-functional team of 30+ student engineers through the full vehicle development lifecycle, from concept to competition."
        } as CaptionMap,
        details: `
            LEADERSHIP & ENGINEERING:
            Served as Mechanical Lead for the 'Apollo' Urban Concept vehicle, directing a team of 30 engineers to a 2nd place podium finish.

            SUSPENSION DESIGN:
            - **Kinematics:** Engineered custom double-wishbone front and trailing-arm rear suspension using Lotus Shark and MATLAB.
            - **Optimization:** Minimized rolling resistance and weight to maximize energy efficiency.
            
            VALIDATION:
            - **Testing:** Directed the design of an in-house motor dynamometer to characterize powertrain efficiency maps.
            - **Telemetry:** Developed a custom data acquisition system to validate vehicle dynamics and performance on the track.
        `,
        tags: ["Vehicle Dynamics", "Leadership", "Suspension Design", "Testing"],
    },

    {
        title: "Nonlinear MPC for Cabin Comfort",
        brief: "Optimization-based thermal control reducing cabin warm-up time by 5x compared to industry-standard PID.",
        image: nmpcLoopHeat, // System Diagram as Thumbnail
        video: "", 
        gallery: [nmpcLoopHeat, nmpcVsPid, nmpcMath, nmpcScale, nmpcLoopCool], 
        captions: {
            [nmpcLoopHeat]: "Figure 1: Heating Loop Architecture. Schematic of the Cabin Thermal Management System modeled in Python, including the heater core, blower, and coolant loops.",
            [nmpcVsPid]: "Figure 2: Performance Benchmark. The Nonlinear MPC (above) reaches the target comfort temperature in 20 seconds, whereas the Autotuned PID (below) takes ~100 seconds due to actuator saturation limits.",
            [nmpcMath]: "Figure 3: Optimal Control Formulation. Definition of the cost function J(x,u) balancing tracking performance against energy consumption, subject to nonlinear state and input constraints.",
            [nmpcScale]: "Figure 4: Proper state scaling and normalization techniques ensured the same controller can be used even when evaluating rescaled system design concepts.",
            [nmpcLoopCool]: "Figure 5: Cooling Architecture. Complementary Vapor Compression Cycle model used for AC pull-down scenarios, being integrated into the unified thermal management framework."
        } as CaptionMap,
        details: `
            CONTROL STRATEGY:
            Designed a Nonlinear Model Predictive Controller (NMPC) to optimize cabin thermal comfort while minimizing energy consumption.
            
            KEY ACHIEVEMENTS:
            - **Performance:** Reduced cabin heating warm-up time from 100s (PID) to 20s (NMPC) by optimally driving actuators to their limits without overshoot.
            - **Optimization:** Formulated the Optimal Control Problem (OCP) using Scipy in Python, utilizing SLSQP.
            - **Robustness:** Implemented rigorous state and control scaling to handle the stiff dynamics of the thermal system, ensuring solver convergence faster than real time requirements.
        `,
        tags: ["Model Predictive Control", "Python", "Optimization"],
    },

    {
        title: "Graph-Based Thermal Modeling Framework",
        brief: "Automated modeling architecture using Graph Theory to synthesize high-fidelity thermal loops for GPU-accelerated simulation.",
        image: graphConcept, // The Clean Infographic
        video: "", 
        gallery: [graphConcept, graphMatrix, graphPipeline, graphVcs], 
        captions: {
            [graphConcept]: "Figure 1: Graph-Theoretic Concept. Visualizing the workflow from 'Building Blocks' (Components) to 'Cycles' (Loops) to the final 'Directed Graph' representation used for equation generation.",
            [graphMatrix]: "Figure 2: Matrix Stacking. Automatic generation of the global system incidence matrix, ensuring mathematically consistent conservation of mass and energy across all component connections.",
            [graphPipeline]: "Figure 3: Compilation Pipeline. The model topology is processed via Jinja templates into JAX code, enabling XLA compilation for massive parallelization on GPUs (Collaborative Framework).",
            [graphVcs]: "Figure 4: Vapor Compression System (VCS) Validation. Open-loop simulation of the VCS component models tracking the HWFET drive cycle, demonstrating stable dynamics under transient heat loads."
        } as CaptionMap,
        details: `
            CORE INNOVATION:
            Pioneered the graph-theoretic formulation for modular thermal modeling, developing the logic to represent single-phase loops as directed graphs. This allows for the automatic generation of conservation equations, eliminating manual connection errors.

            FRAMEWORK & APPLICATION:
            - **Theory:** Formulated the matrix-based stacking approach to assemble individual component ODEs into a global system DAE.
            - **Implementation:** Integrated this graph logic into a high-performance JAX/Jinja framework (developed in collaboration with lab partners) to enable differentiable simulation.
            - **Vapor Compression:** Developed and validated physics-based component models (Compressor, Condenser, Evaporator) within this framework, successfully simulating 2-phase dynamics on the HWFET drive cycle.
        `,
        tags: ["Graph Theory", "JAX/Python", "Thermal Modeling", "GPU Simulation"],
    },
];

// --- HELPER 1: YOUTUBE EMBED ---
const getEmbedUrl = (url: string) => {
    if (!url) return undefined;
    const videoId = url.split('v=')[1];
    if (!videoId) return undefined;
    return `https://www.youtube.com/embed/${videoId ? videoId.split('&')[0] : ''}`;
};

// --- HELPER 2: PARSE BOLD TEXT ---
// This safely converts **text** into <strong>text</strong>
const parseText = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={index} className="highlight-text">{part.slice(2, -2)}</strong>;
        }
        return part;
    });
};

// --- HELPER 3: RENDER DETAILS ---
const renderDetails = (details: string) => {
    if (!details) return null;
    const lines = details.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    return lines.map((line, index) => {
        if (line.endsWith(':')) {
            return (
                <Typography key={index} className="detail-header">
                    {line.slice(0, -1)} {/* Remove the colon for cleaner header */}
                </Typography>
            );
        } else if (line.startsWith('-')) {
            return (
                <li key={index} className="detail-bullet">
                    {parseText(line.substring(1).trim())}
                </li>
            );
        } else {
            return (
                <Typography key={index} paragraph className="detail-text">
                    {parseText(line)}
                </Typography>
            );
        }
    });
};

function Project() {
    const [open, setOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState<any>(null);
    const [activeMedia, setActiveMedia] = useState<string | null>(null); 
    const [isZoomed, setIsZoomed] = useState(false);

    const handleClickOpen = (project: any) => {
        setSelectedProject(project);
        setActiveMedia(project.video ? "video" : project.image);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setTimeout(() => {
            setSelectedProject(null);
            setActiveMedia(null);
        }, 300);
    };

    return (
        <div className="projects-container" id="projects">
            <h1>Engineering Projects</h1>
            
            <div className="projects-grid">
                {projects.map((project, index) => (
                    <div 
                        className="project-card" 
                        key={index} 
                        onClick={() => handleClickOpen(project)}
                    >
                        <div className="image-wrapper">
                            <img src={project.image} alt="thumbnail" />
                            <div className="overlay">
                                {project.video ? 
                                    <YouTubeIcon style={{ fontSize: 50 }} /> : 
                                    <CollectionsIcon style={{ fontSize: 40 }} />
                                }
                            </div>
                        </div>

                        <div className="card-content">
                            <h2>{project.title}</h2>
                            <p>{project.brief}</p>
                            
                            <div className="tags-row">
                                {project.tags.slice(0, 3).map((tag: string, i: number) => (
                                    <Chip 
                                        key={i} 
                                        label={tag} 
                                        size="small" 
                                        className="tech-chip"
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* MAIN PROJECT MODAL */}
            {/* MAIN PROJECT MODAL */}
            <Dialog 
                open={open} 
                onClose={handleClose} 
                maxWidth="xl"
                fullWidth={true}
                className="project-dialog"
                PaperProps={{
                    style: { 
                        backgroundColor: '#1a1a1a', 
                        borderRadius: '12px',
                        maxWidth: '1350px',
                        margin: '20px',
                        maxHeight: '90vh',
                        // FIX 3: SIMPLIFY SHADOW
                        // Reduced spread radius for better performance
                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)' 
                    }
                }}
                slotProps={{
                    backdrop: {
                        style: { 
                            // FIX 4: REMOVE BLUR (Or reduce drastically to '3px')
                            // A darker overlay is much lighter on the GPU
                            backgroundColor: 'rgba(0, 0, 0, 0.85)', 
                            backdropFilter: 'none' 
                        }
                    }
                }}
            >
                {selectedProject && (
                    <div className="modal-layout">
                        {/* LEFT COLUMN: MEDIA */}
                        <div className="media-column">
                            <div className="hero-view">
                                {activeMedia === "video" && selectedProject.video ? (
                                    <iframe 
                                        src={getEmbedUrl(selectedProject.video)} 
                                        title="Project Video" 
                                        frameBorder="0" 
                                        allowFullScreen
                                    />
                                ) : (
                                    <div className="img-container" onClick={() => setIsZoomed(true)}>
                                        <img src={activeMedia || selectedProject.image} alt="Hero" />
                                        {activeMedia && selectedProject.captions && selectedProject.captions[activeMedia] && (
                                            <div className="caption-overlay">
                                                {selectedProject.captions[activeMedia]}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="gallery-strip">
                                {selectedProject.video && (
                                    <div 
                                        className={`strip-item video-thumb ${activeMedia === "video" ? 'active' : ''}`}
                                        onClick={() => setActiveMedia("video")}
                                    >
                                        <PlayCircleFilledWhiteIcon />
                                        <span>Watch Video</span>
                                    </div>
                                )}
                                {selectedProject.gallery.map((imgSrc: string, idx: number) => (
                                    <div 
                                        key={idx} 
                                        className={`strip-item ${activeMedia === imgSrc ? 'active' : ''}`}
                                        onClick={() => setActiveMedia(imgSrc)}
                                    >
                                        <img src={imgSrc} alt={`Thumb ${idx}`} />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* RIGHT COLUMN: DETAILS */}
                        <div className="details-column">
                            {/* CLOSE BUTTON */}
                            <div className="close-btn-circle" onClick={handleClose}>
                                <CloseIcon style={{ fontSize: 20 }} />
                            </div>

                            <Typography variant="h4" className="modal-title"> {/* CHANGED to h4 for sleekness */}
                                {selectedProject.title}
                            </Typography>
                            
                            <Box className="modal-tags">
                                {selectedProject.tags.map((tag: string, i: number) => (
                                    <Chip key={i} label={tag} className="tech-chip-large" />
                                ))}
                            </Box>

                            <div className="divider"></div>

                            <Box className="modal-text">
                                {renderDetails(selectedProject.details)}
                            </Box>
                        </div>
                    </div>
                )}
            </Dialog>

            {/* FULL SCREEN ZOOM MODAL */}
{/* FULL SCREEN ZOOM MODAL */}
            <Dialog
                open={isZoomed}
                onClose={() => setIsZoomed(false)}
                maxWidth={false}
                // FIX 1: Remove the default paper background/shadow completely
                PaperProps={{ 
                    style: { 
                        backgroundColor: 'transparent', 
                        boxShadow: 'none',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    } 
                }}
                // FIX 2: Optimized Backdrop (No Blur, Darker Color)
                slotProps={{
                    backdrop: {
                        style: { 
                            backgroundColor: 'rgba(0, 0, 0, 0.95)', // Darker = Focus
                            backdropFilter: 'none', // <--- THE PERFORMANCE FIX
                            transition: 'opacity 0.3s ease'
                        }
                    }
                }}
            >
                <img 
                    src={activeMedia || ""} 
                    alt="Zoom" 
                    style={{ 
                        maxWidth: '95vw', 
                        maxHeight: '95vh', 
                        objectFit: 'contain', 
                        // FIX 3: Remove massive shadow (expensive)
                        boxShadow: 'none', 
                        // FIX 4: Hardware acceleration for the pop-in
                        willChange: 'transform, opacity',
                        cursor: 'zoom-out'
                    }}
                    onClick={() => setIsZoomed(false)}
                />
            </Dialog>
        </div>
    );
}

export default Project;